// The notification center, from the screens (PLAN-2026-09-26-bell B2, K4).
//
// IN-APP IS THE RECORD (NOTIFICATIONS.md §2): every notification the server
// wrote is in `GET /api/me/notifications`, and read state lives there, so one
// read on the phone is read on the desktop. This holds the bell's count and,
// while the center is open, the list — one copy for the whole shell.
//
// THE COUNT IS ASKED FOR ON A TIMER (B-189), from the pulse's `unread`
// (`/api/me/pulse`, 2026-10-10, which is `/api/me`'s count and its two other
// parts that move without the account's leagues): every half minute while the
// page is visible, at once when it becomes visible again, and after anything
// here changes it. A push, when this device has one, arrives on its own; the
// count catches up on the next ask.
//
// *MARK ALL READ* SENDS THE LIST'S `mark` (B1), never a time: a row written
// after the list was read stays unread, however old the thing it is about.
//
// A SIGNED-OUT VISITOR HAS NO RECORD, and the tour has none: the bell is not
// drawn for them (`AppShell`), and nothing here asks.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MePulse,
  NotificationPage,
  UnreadCount,
  type NoticesKeptShape,
  type NotificationKindId,
  type NotificationPageShape,
  type NotificationViewShape,
} from '@fsp/domain';
import { get, messageOf, send } from './api';
import { GONE, NOT_NOW } from './kept';
import { onDevice } from './keptIdb';
import { newsOfPulse, onAskMe, publishMe } from './meFeed';
import { settleTray } from './deviceTray';
import { settleDevice } from './push';
import { useSession } from './session';

/** How often the bell's count is asked for while the page is visible (B-189). */
export const BELL_ASK_MS = 30_000;

export type Notification = NotificationViewShape;

export interface NotificationsValue {
  /** Null until the first answer, and for nobody signed in. */
  unread: number | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** The list, newest first, as far back as has been asked for. */
  items: Notification[];
  state: 'idle' | 'loading' | 'ready' | 'failed';
  problem: string | null;
  more: boolean;
  /** How long the server keeps a notice, from the list's first page; null until it is read. */
  kept: NoticesKeptShape | null;
  /**
   * The kind the list shows, or null for every kind with a league's week
   * folded (PLAN-2026-10-18-families-and-newcomers §6).
   */
  kind: NotificationKindId | null;
  setKind: (kind: NotificationKindId | null) => void;
  /** Unread rows by kind, from the list's first page: the chips' counts. */
  unreadByKind: Partial<Record<NotificationKindId, number>>;
  loadOlder: () => Promise<void>;
  /** Mark it read and go where it is about, closing the center. */
  land: (n: Notification) => void;
  /** Read through the newest row shown (B1's `through`). */
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { session, reconciled } = useSession();
  const userId = session?.userId ?? null;
  const navigate = useNavigate();

  // THIS DEVICE'S PUSH IS THE SIGNED-IN PERSON'S OR NOBODY'S (`settleDevice`),
  // decided once the session is known — never on the render before, when a
  // cold load has nobody yet and would drop a subscription that is theirs.
  useEffect(() => {
    if (reconciled) void settleDevice(userId);
  }, [reconciled, userId]);
  const [unread, setUnread] = useState<{ userId: string; n: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<{
    userId: string;
    items: Notification[];
    cursor: string | null;
    /** The first page's `mark`: how far the record went when it was read (B1). */
    mark: string | null;
    kept: NoticesKeptShape;
    /** The kind this list is of; null for every kind. */
    kind: NotificationKindId | null;
    unreadByKind: Partial<Record<NotificationKindId, number>>;
  } | null>(null);
  const [kind, setKind] = useState<NotificationKindId | null>(null);
  const [state, setState] = useState<NotificationsValue['state']>('idle');
  const [problem, setProblem] = useState<string | null>(null);
  const control = useRef<AbortController | null>(null);
  /** The next answer settles the tray whatever it says: the first, and the first back on screen. */
  const settleNext = useRef(true);

  const askCount = useCallback(async () => {
    if (userId === null) return;
    const askedAt = Date.now();
    const reply = await get('/me/pulse', MePulse);
    if (reply.status !== 'ok' || reply.data.userId !== userId) return;
    // The same answer carries the account's choices (M2) and its finds (A4):
    // handed on, so they follow the person on this rhythm without a request
    // of their own.
    publishMe(newsOfPulse(reply.data), askedAt);
    const n = reply.data.unread;
    const fresh = settleNext.current;
    settleNext.current = false;
    // THE SAME COUNT IS THE SAME OBJECT, so the tray below is not settled
    // again for an answer that moved nothing.
    setUnread((u) =>
      !fresh && u !== null && u.userId === userId && u.n === n ? u : { userId, n },
    );
  }, [userId]);

  // The count, on a timer while the page is visible — and at once when an
  // action earned an achievement (`askMeNow`, A4), so its moment does not
  // wait for the next tick.
  useEffect(() => {
    if (userId === null) return;
    const now = () => {
      if (!document.hidden) void askCount();
    };
    const back = () => {
      if (document.hidden) return;
      settleNext.current = true;
      void askCount();
    };
    now();
    const timer = window.setInterval(now, BELL_ASK_MS);
    document.addEventListener('visibilitychange', back);
    const stopAsking = onAskMe(() => void askCount());
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', back);
      stopAsking();
    };
  }, [userId, askCount]);

  /** The first page again, replacing what is held. */
  const askFirst = useCallback(async () => {
    if (userId === null) return;
    control.current?.abort();
    const c = new AbortController();
    control.current = c;
    setState((s) => (s === 'ready' ? s : 'loading'));
    const asked = kind;
    const path = asked === null ? '/me/notifications' : `/me/notifications?kind=${asked}`;
    const account = onDevice.account();
    const reply = await get(path, NotificationPage, { signal: c.signal });
    if (c.signal.aborted) return;
    if (reply.status !== 'ok') {
      setProblem(messageOf(reply, 'Your notifications could not be read.'));
      setState('failed');
      // WHAT THIS DEVICE KEPT of the first page (B-557), under the sentence,
      // while nothing newer is held for this kind.
      if (reply.status === 'refused' && GONE.has(reply.code)) onDevice.drop(account, path);
      else if (reply.status !== 'refused' || NOT_NOW.has(reply.code)) {
        const copy = await onDevice.find(account, path);
        const page = copy === null ? null : NotificationPage.safeParse(copy.data);
        if (page !== null && page.success && !c.signal.aborted)
          setList((l) =>
            l !== null && l.userId === userId && l.kind === asked ? l : pageOf(page.data),
          );
      }
      return;
    }
    onDevice.keep(account, path, { data: reply.data, asOf: '' });
    setProblem(null);
    setList(pageOf(reply.data));
    setState('ready');

    function pageOf(page: NotificationPageShape) {
      return {
        userId: userId!,
        items: page.items,
        cursor: page.nextCursor,
        mark: page.mark,
        kept: page.kept,
        kind: asked,
        unreadByKind: page.unreadByKind,
      };
    }
  }, [userId, kind]);

  // Opening the center reads the list afresh, and the count with it.
  useEffect(() => {
    if (!open || userId === null) return;
    void askFirst();
    void askCount();
  }, [open, userId, askFirst, askCount]);

  // AN ANSWER FOR SOMEBODY ELSE IS NOT AN ANSWER (`read.ts`): after a sign-out
  // and a sign-in as somebody else, the last person's list is never shown.
  // AND A LIST OF ANOTHER KIND IS NOT THIS CHIP'S LIST: until its own answer
  // lands, the center shows it loading rather than the last kind's rows.
  const mine = list !== null && list.userId === userId && list.kind === kind ? list : null;
  const count = unread !== null && unread.userId === userId ? unread.n : null;

  // THE TRAY AND THE ICON FOLLOW THE COUNT (B-241), and more than a changed
  // one: a push that arrived and was read elsewhere while this app was away
  // leaves the count where it was, and is exactly the one to close. So the
  // first answer on opening and on coming back settles the tray whatever it
  // says, as does a read here; on screen, an answer that moved the count does
  // (`unread` is a new object then). Settling reads the record's first page
  // when the tray holds a push, so it is not done twice a minute for nothing
  // (2026-10-10). Signed out, nothing is anybody's: the tray is emptied and
  // the count taken off.
  useEffect(() => {
    if (unread !== null && unread.userId === userId) void settleTray(unread.n);
  }, [unread, userId]);
  useEffect(() => {
    if (reconciled && userId === null) void settleTray(0);
  }, [reconciled, userId]);

  const loadOlder = useCallback(async () => {
    if (mine === null || mine.cursor === null || userId === null) return;
    const reply = await get(
      `/me/notifications?cursor=${encodeURIComponent(mine.cursor)}${
        mine.kind === null ? '' : `&kind=${mine.kind}`
      }`,
      NotificationPage,
    );
    if (reply.status !== 'ok') {
      setProblem(messageOf(reply, 'Older notifications could not be read.'));
      return;
    }
    // FROM THE LIST AS IT NOW STANDS, not as it stood when Older was tapped:
    // a Mark all read in between must not come back unread (the review of B4).
    setList((l) =>
      l === null || l.userId !== userId || l.cursor !== mine.cursor
        ? l
        : {
            ...l,
            items: [
              ...l.items,
              ...reply.data.items.filter((n) => !l.items.some((m) => m.id === n.id)),
            ],
            cursor: reply.data.nextCursor,
          },
    );
  }, [mine, userId]);

  const markLocally = useCallback((read: (n: Notification) => boolean) => {
    const at = new Date().toISOString();
    setList((l) =>
      l === null
        ? l
        : {
            ...l,
            items: l.items.map((n) => (n.readAt === null && read(n) ? { ...n, readAt: at } : n)),
          },
    );
  }, []);

  const land = useCallback(
    (n: Notification) => {
      setOpen(false);
      if (n.readAt === null && userId !== null) {
        // A FOLDED WEEK IS READ WHOLE (§6.2): every row it stands for.
        const ids = n.groupIds.length > 0 ? n.groupIds : [n.id];
        markLocally((m) => m.id === n.id);
        setUnread((u) => (u === null ? u : { ...u, n: Math.max(0, u.n - 1) }));
        void send('POST', '/me/notifications/read', { ids }, UnreadCount).then((r) => {
          if (r.status === 'ok') setUnread({ userId, n: r.data.unread });
        });
      }
      navigate(n.navigate);
    },
    [navigate, markLocally, userId],
  );

  // THROUGH THE LIST'S MARK, so a row written since the list was read stays
  // unread; then the list afresh, which is the truth about which that was.
  const markAllRead = useCallback(async () => {
    const mark = mine?.mark ?? null;
    if (mark === null || userId === null) return;
    const reply = await send('POST', '/me/notifications/read', { through: mark }, UnreadCount);
    if (reply.status !== 'ok') {
      setProblem(messageOf(reply, 'That did not go through. Try again.'));
      return;
    }
    markLocally(() => true);
    setUnread({ userId, n: reply.data.unread });
    void askFirst();
  }, [mine, userId, markLocally, askFirst]);

  // A TAP ON A PUSH (the service worker, `public/sw.js`): an open window is
  // told where to go; a window it had to open carries the id in its hash.
  // Either way the app routes it and the notification is read, as a tap in
  // the center would do.
  const readFromPush = useCallback(
    (id: string | null) => {
      if (id === null || userId === null) return;
      markLocally((m) => m.id === id);
      void send('POST', '/me/notifications/read', { ids: [id] }, UnreadCount).then((r) => {
        if (r.status === 'ok') setUnread({ userId, n: r.data.unread });
      });
    },
    [userId, markLocally],
  );
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; path?: string; id?: string | null } | null;
      if (data?.type !== 'fsp.notification.open' || typeof data.path !== 'string') return;
      setOpen(false);
      navigate(data.path);
      readFromPush(data.id ?? null);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, readFromPush]);
  useEffect(() => {
    const found = /^#fsp-read=(.+)$/.exec(window.location.hash);
    if (found === null || userId === null) return;
    // The router's own state kept: its number for the entry is how Back finds
    // the screen before.
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
    readFromPush(decodeURIComponent(found[1]!));
  }, [userId, readFromPush]);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const value = useMemo<NotificationsValue>(
    () => ({
      unread: count,
      open: open && userId !== null,
      setOpen,
      toggle,
      items: mine?.items ?? [],
      state: mine === null && state === 'ready' ? 'loading' : state,
      problem,
      more: mine?.cursor != null,
      kept: mine?.kept ?? null,
      kind,
      setKind,
      unreadByKind: mine?.unreadByKind ?? {},
      loadOlder,
      land,
      markAllRead,
    }),
    [count, open, userId, toggle, mine, state, problem, loadOlder, land, markAllRead, kind],
  );
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside NotificationsProvider');
  return ctx;
}

/**
 * When, as a row says it: "Just now", "12 min ago", "3 h ago", then the
 * weekday and time for this week, then the date. In the device's zone — a
 * notification is about when it reached *me*.
 */
export function whenLabel(iso: string, now: number, zone?: string): string {
  const at = Date.parse(iso);
  const mins = Math.floor((now - at) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const date = new Date(at);
  if (hours < 24 * 6)
    return date.toLocaleString('en-US', {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: zone,
    });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: zone });
}
